/**
 * @fileoverview Status Effects Test Suite
 *
 * Tests for status effects that pieces can apply to other pieces.
 * Covers: Poison, Entangle, Zombify, Infect, Shadow, Meadow, Faint
 */

import { PieceType } from "../../zodiacEngine/types";
import { IntegrationTest, TestSuite } from "../types";

export const statusEffectTests: IntegrationTest[] = [
  // ============ FROG POISON ============
  // Frog captures animal piece and applies poison (skip next turn)
  {
    name: "Frog poison application",
    description: "Monkey captures frog and gets poisoned",
    inputFEN: "CK7/8/M7/1f6/7bk",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Monkey C1->D2",
    expectedFEN: "CK7/8/8/1M!6/7bk",
    expectedDelta: {
      pieceMoved: "Light Monkey captured Dark Frog [C1 -> D2] Light Monkey is now poisoned",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ FUNGI ZOMBIFY ============
  // Fungi captures piece and zombifies it (switches teams for 1 turn)
  {
    name: "Fungi zombification",
    description: "Light Fungi captures Dark Bear and zombifies it",
    inputFEN: "8/8/UK7/1b6/7ck",
    lightKing: PieceType.Fungi,
    darkKing: PieceType.Crane,
    move: "Light Fungi C1->D2",
    expectedFEN: "8/8/8/1UK6/7ck",
    expectedDelta: {
      pieceMoved: "Light Fungi captured Dark Bear [C1 -> D2]",
      statusChanged: false,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ OCTOPUS ENTANGLE ============
  // Octopus stays in place (noMove) and entangles diagonally adjacent enemy piece
  // Octopus at C1 entangles Dark Crane at D2 (diagonal)
  {
    name: "Octopus still entangle",
    description: "Light Octopus entangles diagonally adjacent Dark Crane",
    inputFEN: "8/8/OK7/1c6/7bk",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C1->C1",
    expectedFEN: "8/8/OK7/1c⛓6/7bk",
    expectedDelta: {
      pieceMoved: "Light Octopus entangled Dark Crane [D2]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ OCTOPUS MOVE ENTANGLE ============
  // Octopus triple entangle: moves perpendicular while entangling orthogonally adjacent enemy
  // Octopus at C1, enemy Crane orthogonally at D1 (same row), Octopus moves to C2 while entangling D1
  {
    name: "Octopus move entangle",
    description: "Light Octopus moves to C2 and entangles Dark Crane at D1",
    inputFEN: "8/8/OK7/c7/7bk",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C1->D1:entangle",
    expectedFEN: "8/8/1OK6/c⛓7/7bk",
    expectedDelta: {
      pieceMoved: "Light Octopus moved [C1 -> C2] and Entangled Dark Crane [D1]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ OCTOPUS SHADOW/INK ============
  // Octopus inks to an orthogonal position, creating shadows at other empty orthogonal squares
  // Octopus at C1 inks to C2, shadows created at B1 and D1
  {
    name: "Octopus ink shadow",
    description: "Light Octopus inks to C2, creating shadows at B1 and D1",
    inputFEN: "8/8/OK7/8/7bk",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C1->C2:ink",
    expectedFEN: "8/O7/1OK6/O7/7bk",
    expectedDelta: {
      pieceMoved: "Light Octopus used ink on [C1]",
      statusChanged: false,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ TREE MEADOW ============
  // Fungi creates meadow that blocks trees from moving
  // Fungi at C1 moves to C2, Dark Tree at D2 becomes meadowed (adjacent to C2)
  {
    name: "Fungi meadow blocks tree",
    description: "Light Fungi creates meadow near Dark Tree",
    inputFEN: "8/8/UK7/2t5/7bk",
    lightKing: PieceType.Fungi,
    darkKing: PieceType.Bear,
    move: "Light Fungi C1->C2",
    expectedFEN: "8/8/1UK6/2t⚘5/7bk",
    expectedDelta: {
      pieceMoved: "Light Fungi moved [C1 -> C2] Dark Tree is now meadowed",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  {
    name: "Fungi meadow unblocks tree",
    description: "Light Fungi removes meadow from nearby tree",
    inputFEN: "8/8/1UK6/2t⚘5/7bk",
    lightKing: PieceType.Fungi,
    darkKing: PieceType.Bear,
    move: "Light Fungi C2->C1",
    expectedFEN: "8/8/UK7/2t5/7bk",
    expectedDelta: {
      pieceMoved: "Light Fungi moved [C2 -> C1] Dark Tree is no longer meadowed",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ VIRUS INFECT ============
  // Virus infects piece (virus stays on host, captures when leaving)
  // This test has a valid target at E1 (Dark Bear), so bonus turn is granted
  {
    name: "Virus infection with bonus turn",
    description: "Light Virus infects Light Bear, bonus turn granted (target at E1)",
    inputFEN: "V7/8/BK6bk/8/W7",
    lightKing: PieceType.Bear,
    darkKing: PieceType.Bear,
    move: "Light Virus A1->C1",
    expectedFEN: "8/8/B♛+V6b♕/8/W7",
    expectedDelta: {
      pieceMoved: "Light Virus infected Light Bear [A1 -> C1]",
      statusChanged: true,
      turnChanged: false, // Virus infection grants bonus turn (target at E1)
      bonusTurn: true,
      gameEnded: false,
    },
  },

  // Virus infects piece but NO bonus turn (no valid targets from landing position)
  // Virus at A1 infects Bear at C1, but no other animals within range
  {
    name: "Virus infection without bonus turn",
    description: "Light Virus infects Light Bear, no bonus turn (no targets)",
    inputFEN: "V7/8/BK7/8/7bk",
    lightKing: PieceType.Bear,
    darkKing: PieceType.Bear,
    move: "Light Virus A1->C1",
    expectedFEN: "8/8/B♛+V7/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Virus infected Light Bear [A1 -> C1]",
      statusChanged: true,
      turnChanged: true, // No bonus turn - no valid targets from C1
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ GOAT FAINT ============
  // Goat faints (drops status, removes all effects)
  {
    name: "Goat faint",
    description: "Light Goat faints and drops all status effects",
    inputFEN: "8/8/GK6bk/8/8",
    lightKing: PieceType.Goat,
    darkKing: PieceType.Bear,
    move: "Light Goat C1->C1:faint",
    expectedFEN: "8/8/GK$6bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Goat fainted [C1]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ POISON PREVENTS MOVEMENT ============
  // Poisoned piece cannot move on its turn
  {
    name: "Poisoned piece skips turn",
    description: "Light Monkey gets poisoned",
    inputFEN: "CK7/8/8/1Mf5/7bk",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Monkey D2->D3:capture",
    expectedFEN: "CK7/8/8/2M!5/7bk",
    expectedDelta: {
      pieceMoved: "Light Monkey captured Dark Frog [D2 -> D3] Light Monkey is now poisoned",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ UNENTANGLE MOVEMENT ============
  // Piece becomes unentangled via moving away
  {
    name: "Piece becomes entangled",
    description: "Dark Crane become unentangled",
    inputFEN: "8/8/OK7/1c⛓6/7bk",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C1->B1",
    expectedFEN: "8/OK7/8/1c6/7bk",
    expectedDelta: {
      pieceMoved: "Light Octopus moved [C1 -> B1]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ SNAKE SHED RESPAWN ============
  // Snake uses shed ability to respawn at back rank after being captured
  {
    name: "Snake shed respawn",
    description: "Dark Snake gets captured but respawns",
    inputFEN: "8/8/S*K2s*4/8/7ck",
    lightKing: PieceType.Snake,
    darkKing: PieceType.Crane,
    move: "Light Snake C1->C4:capture",
    expectedFEN: "8/8/3S*K3s/8/7ck",
    expectedDelta: {
      pieceMoved: "Light Snake captured Dark Snake [C1 -> C4] Dark Snake used Shed and respawned [C8]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ TREE CRUSH FAINTED GOAT ============
  // Tree can crush fainted goats
  {
    name: "Tree crushes fainted goat",
    description: "Light Tree crushes Dark fainted Goat",
    inputFEN: "8/8/TK7/g$7/7bk",
    lightKing: PieceType.Tree,
    darkKing: PieceType.Bear,
    move: "Light Tree C1->D1",
    expectedFEN: "8/8/8/TK7/7bk",
    expectedDelta: {
      pieceMoved: "Light Tree crushed Fainted Dark Goat [C1 -> D1]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ RHINO TRAMPLE APPLIES MULTIPLE CAPTURES ============
  // Rhino trampling through multiple pieces
  {
    name: "Rhino trample with status effects",
    description: "Light Rhino tramples poisoned and entangled pieces",
    inputFEN: "8/8/RKm!g⛓5/8/7bk",
    lightKing: PieceType.Rhino,
    darkKing: PieceType.Bear,
    move: "Light Rhino C1->C3:trample",
    expectedFEN: "8/8/2RK5/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Rhino trampled Dark Monkey [C2] and Dark Goat [C3] [C1 -> C3]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ ZOMBIFIED PIECE SWITCHES TEAMS ============
  // Fungi captures and zombified piece acts for capturing team
  {
    name: "Zombified piece",
    description: "Dark Bear becomes zombified",
    inputFEN: "CK7/8/1B6/1u6/7ck",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Crane,
    move: "Light Bear C2->D2:capture",
    expectedFEN: "CK7/8/8/1Bz6/7ck",
    expectedDelta: {
      pieceMoved: "Light Bear captured Dark Fungi [C2 -> D2] Light Bear is now zombified",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ SHADOW BLOCKS MOVEMENT ============
  // Octopus shadow blocks piece movement
  {
    name: "Shadow blocks movement",
    description: "Octopus shadow creates shadows",
    inputFEN: "8/8/1OK6/8/7bk",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C2->B2:ink",
    expectedFEN: "8/1OK6/O1O5/1O6/7bk",
    expectedDelta: {
      pieceMoved: "Light Octopus used ink on [C2]",
      statusChanged: false,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

    // ============ Entangle up down ============
  // Octopus split entangle: moving horizontally while entangling, with two landing options
  // "up" = land on row - 1 from midpoint (D2 in this case)
  // "down" = land on row + 1 from midpoint (D4 in this case)
  {
    name: "Octopus two options down",
    description: "Octopus moves diagonal to entangle",
    inputFEN: "7b/8/2OK5/8/2ok5",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Octopus,
    move: "Light Octopus C3->E3:entangle-down",  // "up" = land on D2 (row - 1 from midpoint D3)
    expectedFEN: "7b/8/8/1OK6/2ok!5",
    expectedDelta: {
      pieceMoved: "Light Octopus moved [C3 -> D2] and Entangled Dark Octopus [E3]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

      // ============ Entangle up down ============
  // Octopus split entangle: moving horizontally while entangling, with two landing options
  // "up" = land on row - 1 from midpoint (D2 in this case)
  // "down" = land on row + 1 from midpoint (D4 in this case)
  {
    name: "Octopus two options up",
    description: "Octopus moves diagonal to entangle",
    inputFEN: "7b/8/2OK5/8/2ok5",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Octopus,
    move: "Light Octopus C3->E3:entangle-up",  // "up" = land on D4 (row - 1 from midpoint D3)
    expectedFEN: "7b/8/8/3OK4/2ok!5",
    expectedDelta: {
      pieceMoved: "Light Octopus moved [C3 -> D4] and Entangled Dark Octopus [E3]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ Entangle left right ============
  // Octopus split entangle: moving vertically while entangling, with two landing options
  // "left" = land on col - 1 from midpoint (B3 in this case)
  // "right" = land on col + 1 from midpoint (D3 in this case)
  {
    name: "Octopus two options left",
    description: "Octopus moves diagonal to entangle",
    inputFEN: "7b/8/1OK1ok4/8/8",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Octopus,
    move: "Light Octopus C2->C4:entangle-left",  // "left" = land on B3 (col - 1 from midpoint C3)
    expectedFEN: "7b/2OK5/3ok!4/8/8",
    expectedDelta: {
      pieceMoved: "Light Octopus moved [C2 -> B3] and Entangled Dark Octopus [C4]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  {
    name: "Octopus two options right",
    description: "Octopus moves diagonal to entangle",
    inputFEN: "7b/8/1OK1ok4/8/8",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Octopus,
    move: "Light Octopus C2->C4:entangle-right",  // "right" = land on D3 (col + 1 from midpoint C3)
    expectedFEN: "7b/8/3ok!4/2OK5/8",
    expectedDelta: {
      pieceMoved: "Light Octopus moved [C2 -> D3] and Entangled Dark Octopus [C4]",
      statusChanged: true,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ RHINO BACKSTEP ON INFECTED PENGUIN ============
  // Rhino can backstep onto a virus-infected penguin and becomes infected
  {
    name: "Rhino backstep on infected penguin",
    description: "Light Rhino backsteps diagonally to capture Dark Penguin infected by virus, becoming infected",
    inputFEN: "CK7/8/8/6R*1/5p+v1*bk",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Rhino D7->E6:trample",
    expectedFEN: "CK7/8/8/8/5R*+v1bk",
    expectedDelta: {
      pieceMoved: "Light Rhino captured Dark Penguin [D7 -> E6] Light Rhino is now infected",
      statusChanged: false,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },
];

export const statusEffectsSuite: TestSuite = {
  name: "Status Effects",
  description: "Tests for status effects (Poison, Entangle, Zombify, Infect, Shadow, Meadow, Faint)",
  tests: statusEffectTests,
};

// ============================================================================
// SUPER COMPLEX SCENARIOS - Multi-piece interactions and edge cases
// ============================================================================

export const superComplexTests: IntegrationTest[] = [
  // ============ ZOMBIE INK ============
  // Zombified octopus uses ink, shadows persist for original owner to deal with
  {
    name: "Zombie ink - octopus captures fungi then enemy inks",
    description: "Light Octopus captures Dark Fungi, becomes zombified, Dark uses ink with zombified octopus",
    inputFEN: "7bk/8/1OK6/1u6/8",  // Light Octopus at C2, Dark Fungi at D2, Dark Bear King at A8
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C2->D2:capture",  // First move for compatibility
    expectedFEN: "7bk/8/2OK5/8/8",  // Final expected FEN after all steps
    isSequence: true,
    steps: [
      {
        move: "Light Octopus C2->D2:capture",
        expectedFEN: "7bk/8/8/1OKz6/8",  // Octopus at D2, zombified
        expectedDelta: {
          pieceMoved: "Light Octopus captured Dark Fungi [C2 -> D2] Light Octopus is now zombified",
          statusChanged: true,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        note: "Light Octopus captures Dark Fungi and becomes zombified",
      },
      {
        move: "Dark Octopus D2->C2:ink",  // Dark controls zombie octopus and inks
        expectedFEN: "7bk/8/1OKz6/O1O5/1O6",  // Real at C2, shadows at D1, D3, E2
        expectedDelta: {
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        note: "Dark uses zombie octopus to ink - shadows persist for Light to deal with",
      },
      {
        move: "Light Octopus C2->C3",  // Light picks REAL octopus and moves it
        expectedFEN: "7bk/8/2OK5/8/8",  // Shadows cleared, zombification cleared, octopus at C3
        expectedDelta: {
          statusChanged: true,  // Zombification expires
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        note: "Light finds real octopus, shadows and zombification clear",
      },
    ],
  },

  // ============ ZOMBIE INK - SHADOW TRAP ============
  // Zombified octopus uses ink, owner picks WRONG shadow and gets trapped
  // FEN doesn't change when shadow is picked (shadows disappear, turn ends)
  // Delta inference can only say "Player attempted to move Ink shadow and failed"
  {
    name: "Zombie ink - shadow trap (wrong guess)",
    description: "Light Octopus zombified, Dark inks, Light picks wrong shadow and fails",
    inputFEN: "B6b/C6c/4O2o/U3u3/G6g/M6m",  // Setup with octopi and fungi
    lightKing: PieceType.Bear,
    darkKing: PieceType.Bear,
    move: "Light Octopus C5->C4:capture",  // First move for compatibility
    expectedFEN: "B6b/C3c3/4O2o/U7/G6g/M6m",  // Final expected FEN after Dark Goat moves
    isSequence: true,
    steps: [
      {
        move: "Light Octopus C5->C4:capture",
        expectedFEN: "B6b/C6c/4Oz2o/U7/G6g/M6m",  // Octopus at C4, zombified
        expectedDelta: {
          pieceMoved: "Light Octopus captured Dark Fungi [C5 -> C4] Light Octopus is now zombified",
          statusChanged: true,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        note: "Light Octopus captures Dark Fungi and becomes zombified",
      },
      {
        move: "Dark Octopus C4->C8:ink",  // Dark controls zombie octopus and inks to C8
        expectedFEN: "dB6b/4O2o/U2O1O2/G3Oz2g/M6m",  // Real at C5, shadows at C1, C3, C7
        expectedDelta: {
          pieceMoved: "Light Octopus used ink on [C5]",  // Note: from Light's perspective
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        note: "Dark uses zombie octopus to ink - shadows persist for Light to deal with",
      },
      {
        // Light picks a SHADOW (wrong guess) - shadows are cleared, turn changes
        // The movezombieshadow command clears all shadow pieces and ends the turn
        move: "movezombieshadow",  // Clears shadows + ends turn
        expectedFEN: "dB6b/7o/U7/G3Oz2g/M6m",  // Shadows cleared, only real octopus (Oz) and dark octopus (o) remain
        expectedDelta: {
          pieceMoved: "Light player attempted to move Ink shadow and failed",
          statusChanged: false,
          turnChanged: true,  // Turn passes to Dark
          bonusTurn: false,
          gameEnded: false,
        },
        note: "Light picks wrong shadow - trap triggers, shadows disappear, turn ends.",
      },
      {
        move: "Dark Goat D1->D8:traverse",  // Dark Goat traverses
        expectedFEN: "B6b/C3c3/4O2o/U7/G6g/M6m",
        expectedDelta: {
          pieceMoved: "Dark Goat traversed [D1 -> D8]",
          statusChanged: false,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        note: "Dark continues play after Light's failed shadow guess",
      },
    ],
  },
];

export const superComplexSuite: TestSuite = {
  name: "Super Complex Scenarios",
  description: "Tests for complex multi-piece interactions and edge cases",
  tests: superComplexTests,
};
